import { queryOptions } from "@tanstack/react-query";
import { gql } from "graphql-request";

import { gqlClient } from "../../../shared/lib/gql-client";
import { IMAGE_FIELDS } from "../../images/api/fragments";
import { LINK_FIELDS } from "../../links/api/fragments";
import type { Paged, PageVariables } from "../../../shared/lib/paging";
import type { Organiser } from "../types";

export const ORGANISER_FIELDS = gql`
  ${LINK_FIELDS}
  ${IMAGE_FIELDS}
  fragment OrganiserFields on Organiser {
    id
    name
    description
    contactEmail
    # Registry identity, shown on the detail page. Cheap to select — all three
    # are plain columns on the row the list already reads.
    cvr
    registeredName
    foreningsnr
    # The event form's prefill source. Ids rather than resolved objects: the form
    # binds them straight to its existing venue and area pickers, which already
    # hold the names.
    defaultVenueId
    defaultAreaId
    # Both batched server-side, so a page of 20 organisers is one query each.
    links {
      ...LinkFields
    }
    images {
      ...ImageFields
    }
  }
`;

const ORGANISERS = gql`
  ${ORGANISER_FIELDS}
  query Organisers {
    organisers {
      ...OrganiserFields
    }
  }
`;

const ORGANISER = gql`
  ${ORGANISER_FIELDS}
  query Organiser($id: ID!) {
    organiser(id: $id) {
      ...OrganiserFields
    }
  }
`;

/**
 * The paginated list, for the organisers page.
 *
 * Alongside `ORGANISERS`, which the event form's picker needs in full — same
 * reasoning as venues.
 */
const ORGANISER_PAGE = gql`
  ${ORGANISER_FIELDS}
  query OrganiserPage($limit: Int, $offset: Int) {
    organiserPage(limit: $limit, offset: $offset) {
      items {
        ...OrganiserFields
      }
      totalCount
    }
  }
`;

export const organiserQueries = {
  all: () => ["organisers"] as const,
  lists: () => [...organiserQueries.all(), "list"] as const,
  details: () => [...organiserQueries.all(), "detail"] as const,
  /**
   * One organiser, for the detail page.
   *
   * Its own query rather than picking the row out of the cached list. The list is
   * held for five minutes, so a detail page opened from a stale cache would show
   * an organiser someone else has since corrected — and this page is where the
   * registry fields are edited, which is exactly the wrong place to be a few
   * minutes behind. Both keys sit under `all()`, so one invalidation after a save
   * still refreshes both.
   */
  detail: (id: string) =>
    queryOptions({
      queryKey: [...organiserQueries.details(), id] as const,
      queryFn: () =>
        gqlClient.request<{ organiser: Organiser | null }>(ORGANISER, { id }),
    }),
  list: () =>
    queryOptions({
      queryKey: [...organiserQueries.lists()] as const,
      queryFn: () => gqlClient.request<{ organisers: Organiser[] }>(ORGANISERS),
      /*
       * Reference data that changes rarely, read by the event form's picker on
       * every visit — same treatment as venues. Explicit invalidation after
       * create/update keeps it correct without a refetch per mount.
       */
      staleTime: 5 * 60 * 1000,
    }),

  /** One page of organisers, with the total. For the list page, not the picker. */
  page: (variables: PageVariables = {}) =>
    queryOptions({
      queryKey: [...organiserQueries.lists(), "page", variables] as const,
      queryFn: () =>
        gqlClient.request<{ organiserPage: Paged<Organiser> }>(
          ORGANISER_PAGE,
          variables,
        ),
      staleTime: 5 * 60 * 1000,
      placeholderData: (previous) => previous,
    }),
};
